import React, { useEffect, useState } from "react";
import BookingShell, { api } from "./BookingShell";
import { logActivity } from "../../utils/activity";
import { bookedPlayers, extraPlayersPrice } from "./pricing";
import { formatKr, formatTime } from "../../utils/format";

// ExtraPlayersPage works out what a group owes when it turns up with more players than its booking
// paid for. It changes nothing — the GM takes the money and the booking stays as it is.
export default function ExtraPlayersPage() {
    return (
        <BookingShell title="Ekstra spillere" activity="open_extra_players" requireBooking={false}>
            {room => <ExtraPlayers key={room.calendar_id} room={room} />}
        </BookingShell>
    );
}

function ExtraPlayers({ room }) {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [eventId, setEventId] = useState("");
    const [booked, setBooked] = useState("");
    const [arriving, setArriving] = useState("");
    const [vector, setVector] = useState(undefined); // undefined = not loaded, null = no price rule
    const [priceError, setPriceError] = useState("");
    const [registering, setRegistering] = useState(false);
    const [receipt, setReceipt] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api(`gm-booking/bookings?calendar_id=${encodeURIComponent(room.calendar_id)}`)
            .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(data => {
                if (cancelled) return;
                const list = (data.bookings || []).slice().sort((a, b) => a.start_time.localeCompare(b.start_time));
                setBookings(list);
            })
            .catch(() => !cancelled && setError("Kunne ikke hente dagens bookinger."))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [room.calendar_id]);

    const booking = bookings.find(b => b.event_id === eventId) || null;

    useEffect(() => {
        if (!booking) return;
        let cancelled = false;
        setVector(undefined);
        setPriceError("");
        api(`gm-booking/price?calendar_id=${encodeURIComponent(room.calendar_id)}&start=${encodeURIComponent(booking.start_time)}`)
            .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(data => !cancelled && setVector(data.price_per_person ?? null))
            .catch(() => !cancelled && setPriceError("Kunne ikke hente prisen."));
        return () => { cancelled = true; };
    }, [booking, room.calendar_id]);

    function pick(b) {
        setEventId(b.event_id);
        setReceipt(null);
        const m = bookedPlayers(b.players);
        setBooked(m ? String(m) : "");
        setArriving(m ? String(m + 1) : "");
    }

    const result = booking && vector !== undefined && booked && arriving ? extraPlayersPrice(vector, booked, arriving) : null;

    // Registering the sale is what turns it into upsell points for the GM taking the money. The
    // amount is recomputed by games-cloud from the same booking and price rules — the numbers
    // above are what the GM reads, not what the score is based on.
    async function register() {
        setRegistering(true);
        setReceipt(null);
        try {
            const r = await api("gm-booking/extra-players", {
                method: "POST",
                body: JSON.stringify({
                    calendar_id: room.calendar_id,
                    event_id: booking.event_id,
                    booked: Number(booked),
                    arriving: Number(arriving),
                }),
            });
            if (r.status === 201) {
                const data = await r.json();
                logActivity("gm_extra_players", `${room.calendar_id} ${booking.start_time} +${data.extra_players}`);
                setReceipt({
                    ok: true,
                    text: `Registreret: ${data.extra_players} ekstra ${data.extra_players === 1 ? "spiller" : "spillere"}, ${formatKr(data.amount_kr)}` +
                        (data.points ? ` — ${data.points} point til ${data.sold_by}.` : "."),
                });
            } else if (r.status === 409) {
                setReceipt({ ok: false, text: "Bookingen dækker allerede det antal spillere." });
            } else if (r.status === 503) {
                setReceipt({ ok: false, text: "Point kan ikke registreres lige nu. Tag pengene, og sig det til en admin." });
            } else {
                setReceipt({ ok: false, text: "Salget kunne ikke registreres. Prøv igen." });
            }
        } catch {
            setReceipt({ ok: false, text: "Salget kunne ikke registreres. Prøv igen." });
        } finally {
            setRegistering(false);
        }
    }

    return (
        <div className="bk-card">
            <div className="bk-card-head"><h2>Ekstra spillere</h2></div>
            <p className="bk-dim">
                De ekstra spillere betaler prisen pr. person for den holdstørrelse, de ender som. De betalte pladser beholder
                deres pris. Går holdet over det antal, rummet sælges til, bruges prisen for det største hold.
            </p>

            {error && <div className="bk-error">{error}</div>}
            {loading && <div className="bk-empty">Henter…</div>}
            {!loading && !error && bookings.length === 0 && <div className="bk-empty">Ingen bookinger i {room.name} i dag.</div>}

            <ul className="bk-bookings">
                {bookings.map(b => (
                    <li key={b.event_id}>
                        <button type="button" className={`bk-booking ${b.event_id === eventId ? "active" : ""}`} onClick={() => pick(b)}>
                            <span className="bk-slot-time">{formatTime(b.start_time)}</span>
                            <span className="bk-booking-name">{b.customer_name || "Uden navn"}</span>
                            <span className="bk-slot-sub">{b.players ? `${b.players} spillere` : ""}</span>
                        </button>
                    </li>
                ))}
            </ul>

            {booking && (
                <>
                    <div className="bk-grid">
                        <label className="bk-field">
                            Booket til
                            <input type="number" min="1" value={booked} onChange={e => setBooked(e.target.value)} />
                        </label>
                        <label className="bk-field">
                            Kommer som
                            <input type="number" min="1" value={arriving} onChange={e => setArriving(e.target.value)} />
                        </label>
                    </div>
                    {bookedPlayers(booking.players) == null && booking.players && (
                        <div className="bk-note">Bookingen står som "{booking.players}" spillere — skriv hvor mange der er betalt for.</div>
                    )}

                    {priceError && <div className="bk-error">{priceError}</div>}
                    {vector === undefined && !priceError && <div className="bk-empty">Henter pris…</div>}
                    {result?.error === "no_price" && <div className="bk-note">Ingen pris i prisreglerne for dette tidspunkt — brug prislisten.</div>}
                    {result?.error === "not_sold" && <div className="bk-error">Holdstørrelsen {arriving} sælges ikke på dette tidspunkt.</div>}
                    {result?.covered && (
                        <div className="bk-success">
                            Ingen ekstra betaling. Bookingen har allerede betalt for {result.paid} spillere.
                        </div>
                    )}
                    {result?.error === "invalid" && <div className="bk-note">"Kommer som" skal være flere end "Booket til".</div>}
                    {result?.overMax && (
                        <div className="bk-warn">
                            {arriving} spillere er flere, end rummet normalt sælges til — højst {result.max}.
                            De ekstra beregnes til prisen for {result.max} spillere.
                        </div>
                    )}
                    {result && !result.error && !result.covered && (
                        <>
                            {result.paid > Number(booked) && (
                                <div className="bk-note">
                                    Bookingen har betalt for {result.paid} spillere — det er den mindste holdstørrelse, der sælges på dette tidspunkt.
                                </div>
                            )}
                            <div className="bk-price">
                                <span>{result.extra} ekstra {result.extra === 1 ? "spiller" : "spillere"} × {formatKr(result.perPerson)}</span>
                                <strong>{formatKr(result.amount)}</strong>
                            </div>
                            <button
                                type="button"
                                className="topbar-btn accent bk-submit"
                                onClick={register}
                                disabled={registering || receipt?.ok}
                            >
                                {registering ? "Registrerer…" : receipt?.ok ? "Registreret" : `Registrér salg — ${formatKr(result.amount)}`}
                            </button>
                        </>
                    )}
                    {receipt && <div className={receipt.ok ? "bk-success" : "bk-error"}>{receipt.text}</div>}
                </>
            )}
        </div>
    );
}
